import React from 'react';
import {
  EditButton,
  Labeled,
  Show,
  TextField,
  TopToolbar,
  useRecordContext
} from 'react-admin';
import { Box, Breadcrumbs, Container, Grid, Link as MuiLink, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { PaginatedProjectContacts } from './components/PaginatedProjectContacts.tsx';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ p: 2.5, bgcolor: 'common.white', borderRadius: 2, boxShadow: 1, mb: 2.5 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ProjectShowActions = () => (
  <TopToolbar>
    <EditButton />
  </TopToolbar>
);

const ProjectShowContent: React.FC = () => {
  const record = useRecordContext<{ id?: number; name?: string }>();
  if (!record) return null;

  return (
    <Box sx={{ mt: 2, p: 2.5, bgcolor: 'grey.50', borderRadius: 2 }}>
      <Box sx={{ px: 0.5, mb: 1 }}>
        <Typography variant="h5" gutterBottom>
          {record.name ?? 'Project'}
        </Typography>
      </Box>

      <Section title="Project Details">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Labeled label="Name">
              <TextField source="name" />
            </Labeled>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Labeled label="Description">
              <TextField source="description" />
            </Labeled>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Labeled label="General Notes">
              <TextField source="notes" />
            </Labeled>
          </Grid>
        </Grid>
      </Section>

      <Section title="Contacts">
        {record.id ? <PaginatedProjectContacts projectId={record.id} /> : null}
      </Section>

      <NoteListSection entityType="PROJECT" readOnly />
      <Box mt={2.5}>
        <DocumentListSection entityType="PROJECT" readOnly />
      </Box>
    </Box>
  );
};

const ProjectShow: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/projects">
          Projects
        </MuiLink>
        <Typography color="text.primary">View</Typography>
      </Breadcrumbs>
      <Show actions={<ProjectShowActions />}>
        <ProjectShowContent />
      </Show>
    </Container>
  );
};

export default ProjectShow;
